import type { FinancialAssumptions } from '../types/financialAssumptions';
import { dataCenterFinancialModelV1Schema, type DataCenterFinancialModelV1, type DataCenterFinancialResult } from '../types/dataCenterFinancial';
import { discountedSum, solveIrrFraction } from './projectFinance';
import { hashCanonicalJson, sha256Hex } from './reportGeneration/reportContractHash';

export function isDataCenterFinance(value: unknown): value is FinancialAssumptions & { businessModelKind: 'data-center-capacity-fee' } {
  return value != null && typeof value === 'object' && (value as FinancialAssumptions).businessModelKind === 'data-center-capacity-fee';
}

/** DC assumptions belong to the financial report; legacy generation context is unchanged. */
export function financialAssumptionsForReport(assumptions: FinancialAssumptions | null | undefined, reportType: string): FinancialAssumptions | undefined {
  return assumptions && (!isDataCenterFinance(assumptions) || reportType === 'financial') ? assumptions : undefined;
}

export interface DataCenterFinancialSourceDocument {
  id: string;
  contentHash?: string;
  fullExtractedText?: string;
  textContent?: string;
  demonstrationProvenance?: { scenarioId: string; bodySha256: string; originalFileSha256?: string };
}

/** Checked manual writes bind quotes to the actual canonical document body, not a source label. */
export function assertDataCenterFinancialSources(
  model: DataCenterFinancialModelV1,
  siteId: string,
  documents: readonly DataCenterFinancialSourceDocument[],
): void {
  if (model.siteId !== siteId) throw new Error('Financial model belongs to another site.');
  for (const source of model.sources) {
    const doc = documents.find(d => d.id === source.documentId);
    const body = doc?.fullExtractedText ?? doc?.textContent;
    if (!doc || typeof body !== 'string' || sha256Hex(body) !== source.bodySha256
      || doc.contentHash !== source.rawSha256) throw new Error(`Financial source identity mismatch: ${source.documentId}. Reload the exact source body.`);
    const provenance = doc.demonstrationProvenance;
    if (provenance && model.authority !== 'fictional-scenario') throw new Error('Fictional source cannot become an authoritative project financial input.');
    if (model.authority === 'fictional-scenario' && (!provenance || provenance.scenarioId !== model.scenarioId
      || provenance.bodySha256 !== source.bodySha256
      || provenance.originalFileSha256 !== source.rawSha256)) throw new Error('Financial demonstration source stamp mismatch.');
    for (const quote of Object.values(model.quotes).filter(q => q.documentId === doc.id)) {
      if (body.slice(quote.charStart, quote.charEnd) !== quote.quote) throw new Error('Financial quote does not match its exact frozen source span.');
    }
  }
}

/** Pure annual capacity-fee ledger. No PPA proxy, baseline, comparison-output input or source mutation. */
export function computeDataCenterFinance(input: unknown): DataCenterFinancialResult {
  const blank: DataCenterFinancialResult = {
    modelKind: 'data-center-capacity-fee', modelHash: null, computable: false, issues: [], ledger: [],
    modeledCapexUsd: null, npvUsd: null, irrPct: null, paybackYears: null,
    paybackMethod: 'undiscounted-first-cumulative-crossing-linear-within-period',
    paybackClock: 'elapsed-years-from-reference-year-including-construction',
  };
  const parsed = dataCenterFinancialModelV1Schema.safeParse(input);
  if (!parsed.success) return { ...blank, issues: parsed.error.issues.map(i => `${i.path.join('.')}: ${i.message}`) };
  const model = parsed.data;
  const issues: string[] = [];
  for (const field of ['priceYear', 'referenceYear', 'discountRatePct', 'pricedBomUsd', 'contingencyPct'] as const) {
    if (model[field] == null) issues.push(field);
  }
  if (!model.periods.length) issues.push('periods');
  for (const period of model.periods) {
    for (const [field, value] of Object.entries(period)) if (value == null) issues.push(`periods.${period.year}.${field}`);
  }
  if (issues.length) return { ...blank, modelHash: hashCanonicalJson(model), issues };
  const draws = model.periods.reduce((sum, period) => sum + period.constructionDrawPct!, 0);
  if (Math.abs(draws - 100) > 1e-8) return { ...blank, modelHash: hashCanonicalJson(model), issues: ['constructionDrawPct must allocate exactly 100% of the modeled capital.'] };
  const modeledCapexUsd = model.pricedBomUsd! * (1 + model.contingencyPct! / 100);
  let cumulative = 0;
  const ledger = model.periods.map(p => {
    const contractedItKw = p.availableItMw! * 1000 * p.occupancyPct! / 100;
    const gridConsumptionMwh = contractedItKw / 1000 * p.energyUtilizationPct! / 100 * p.pue! * p.energyHours!;
    const capacityRevenueUsd = contractedItKw * p.capacityFeeUsdPerItKwMonth! * p.billingMonths!;
    const gridEnergyUsd = gridConsumptionMwh * 1000 * p.gridEnergyUsdPerKwh!;
    const demandChargeUsd = p.billedFacilityPeakMw! * 1000 * p.demandChargeUsdPerFacilityKwMonth! * p.billingMonths!;
    const maintenanceUsd = modeledCapexUsd * p.commissionedCapexPct! / 100 * p.maintenancePct! / 100;
    const standbyFuelUsd = p.standbyFuelLitres! * p.standbyFuelUsdPerLitre!;
    const constructionDrawUsd = modeledCapexUsd * p.constructionDrawPct! / 100;
    const operatingCashUsd = capacityRevenueUsd - gridEnergyUsd - demandChargeUsd - p.fixedOperatingUsd! - maintenanceUsd - standbyFuelUsd;
    const netCashUsd = operatingCashUsd - constructionDrawUsd - p.overhaulUsd! + p.terminalProceedsUsd!;
    cumulative += netCashUsd;
    return { year: p.year, contractedItKw, gridConsumptionMwh, capacityRevenueUsd, gridEnergyUsd, demandChargeUsd,
      fixedOperatingUsd: p.fixedOperatingUsd!, maintenanceUsd, standbyFuelUsd, constructionDrawUsd,
      overhaulUsd: p.overhaulUsd!, terminalProceedsUsd: p.terminalProceedsUsd!, operatingCashUsd, netCashUsd, cumulativeCashUsd: cumulative };
  });
  if (!Number.isFinite(modeledCapexUsd) || ledger.some(row => Object.values(row).some(v => !Number.isFinite(v)))) {
    return { ...blank, modelHash: hashCanonicalJson(model), issues: ['Annual ledger overflow/non-finite result.'] };
  }
  const flows = ledger.map(row => row.netCashUsd);
  const npvUsd = discountedSum(flows, model.discountRatePct! / 100);
  if (!Number.isFinite(npvUsd)) return { ...blank, issues: ['Non-finite NPV.'] };
  const signs = flows.filter(v => v !== 0).map(v => Math.sign(v));
  const signChanges = signs.reduce((sum, sign, i) => sum + (i > 0 && sign !== signs[i - 1] ? 1 : 0), 0);
  // A scalar solver can select one of several roots in a non-conventional
  // stream. Preserve its arithmetic for conventional streams; do not choose a
  // root from a source's published IRR for a potentially ambiguous stream.
  const irr = signChanges === 1 ? solveIrrFraction(flows) : null;
  const irrPct = irr != null && Number.isFinite(irr) && irr > -1 ? irr * 100 : null;
  let paybackYears: number | null = null;
  for (let i = 1; i < ledger.length; i += 1) {
    const previous = ledger[i - 1]!.cumulativeCashUsd;
    if (previous < 0 && ledger[i]!.cumulativeCashUsd >= 0 && ledger[i]!.netCashUsd > 0) {
      paybackYears = i - 1 + (-previous / ledger[i]!.netCashUsd);
      break;
    }
  }
  return { ...blank, computable: true, issues: [], modelHash: hashCanonicalJson(model), ledger, modeledCapexUsd, npvUsd, irrPct, paybackYears,
    ...(irrPct == null ? { irrUnavailableReason: signChanges === 0 ? 'one-sided-cashflow' : signChanges > 1 ? 'non-conventional-cashflow' : 'no-convergence' } as const : {}) };
}
